import React, { useEffect, useMemo, useState } from "react";
import { Row } from "react-bootstrap";
import { useReactiveVar } from "@apollo/client";
import SocketConnection from "../../../../common/src/services/SocketConnection";
import { LoadingStatus } from "@me-team/common/src/components/LoadingStatus/LoadingStatus";
import { EntityType } from "@me-team/common/src/utils/enums";
import { JobContainerProxy } from "../../../main/graphql/types";
import { useGetJobsQuery } from "../../../main/jobs/graphql/jobs.hooks";
import { Debounce } from "@me-team/common/src/services/Debounce";
import "../../../services/joyride";
import GlobalWidgets from "@me-team/common/src/components/Widgets/GlobalWidgets";
import Layout from "@me-team/common/src/components/Layout";
import NavbarWrapper from "@me-team/common/src/components/NavbarWrapper/NavbarWrapper";
import {
    useGetCompanyAndRoleQuery,
    useGetCompanyAndRoleWithoutOwnerQuery,
} from "../../../main/company/graphql/company.hooks";
import { currentCompanyIdVar, currentBranchIdVar } from "../../../main/globalVar/state";
import { useCurrentBranchAccess } from "../../hooks/useCurrentBranchAccess";
import { useLocation, useMatch, useParams } from "react-router-dom";
import { createContext } from "react";
export const AccessContext = createContext({ isReady: false });

export const App = () => {
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const { branchId: branchIdFromParams } = useParams();
    const currentBranchId = useReactiveVar(currentBranchIdVar);
    const branchId: string = branchIdFromParams ?? String(currentBranchId);
    const isValidCompanyId = !isNaN(+currentCompanyId) && +currentCompanyId > 0;
    const isValidBranchId = Number.isInteger(Number(branchId)) && Number(branchId) > 0;
    const branchIdNumber = isValidBranchId ? Number(branchId) : null;
    const location = useLocation();
    const isCreateOwnerPage = Boolean(useMatch("/create-owner/*"));
    const [jobList, setJobList] = useState<JobContainerProxy>(null);
    const [isLoadingStatusOpen, setIsLoadingStatusOpen] = useState<boolean>(false);
    const [isVisible, setIsVisible] = useState(() => localStorage.getItem("isVisible") === "true");

    const { data: companyAndRoleData, loading: companyAndRoleLoading } = useGetCompanyAndRoleQuery({
        skip: !currentCompanyId || !currentBranchId || isCreateOwnerPage || !isValidBranchId,
        variables: {
            companyId: parseInt(currentCompanyId),
            isOnlyParents: false,
            branchId: branchIdNumber!,
            page: 1,
            itemsPerPage: 10000,
        },
        context: { errorType: "global" },
    });

    const { data: companyAndRoleWithoutOwnerData, loading: companyAndRoleWithoutOwnerLoading } =
        useGetCompanyAndRoleWithoutOwnerQuery({
            skip: !currentCompanyId || !currentBranchId || !isCreateOwnerPage || !isValidBranchId,
            variables: {
                companyId: parseInt(currentCompanyId),
                isOnlyParents: false,
                branchId: branchIdNumber!,
                page: 1,
                itemsPerPage: 10000,
            },
            context: { errorType: "global" },
        });

    const userData = isCreateOwnerPage ? companyAndRoleWithoutOwnerData : companyAndRoleData;
    const userDataLoading = isCreateOwnerPage ? companyAndRoleWithoutOwnerLoading : companyAndRoleLoading;
    const { isAccessReady } = useCurrentBranchAccess({
        branchId: branchIdNumber,
        isCreateOwnerPage,
        userData,
        userDataLoading,
    });

    const { refetch: refetchJobs } = useGetJobsQuery({
        variables: { companyId: Number(currentCompanyId), branchId: Number(branchId) },
        skip: !isValidCompanyId || !currentBranchId,
        onCompleted: (data) => {
            setJobList(data.job);
        },
    });

    useEffect(() => {
        localStorage.setItem("isVisible", isVisible.toString());
    }, [isVisible]);

    useEffect(() => {
        if (!currentCompanyId) return;

        const debouncedRefetchJobs = Debounce(() => {
            refetchJobs().then((data) => {
                setJobList(data.data.job);
                setIsVisible(true);
                setIsLoadingStatusOpen(true);
            });
        }, 500);

        new SocketConnection(`${process.env.REACT_APP_SOCKET}/?companyId=${currentCompanyId}`, (data: any) => {
            if ([EntityType.IMPORT, EntityType.EXPORT, EntityType.REPORT].includes(data.entity)) {
                debouncedRefetchJobs();
            }
        });
    }, [currentCompanyId, refetchJobs]);

    const onCloseLoadingImportHandler = () => {
        const newValue = !isVisible;
        setIsVisible(newValue);
        localStorage.setItem("isVisible", newValue.toString());
    };

    const hasJobs = jobList?.importJobs.length || jobList?.exportJobs.length || jobList?.storageReportJobs.length;
    const isRegistrationOrCreateOwnerOrMain = useMemo(
        () =>
            location.pathname === "/registration" ||
            location.pathname.startsWith("/create-owner") ||
            location.pathname.startsWith("/branches/main/branch"),
        [location.pathname]
    );

    if (!isRegistrationOrCreateOwnerOrMain && (!userData || !isAccessReady)) return <></>;

    return (
        <AccessContext.Provider value={{ isReady: isAccessReady }}>
            <GlobalWidgets />
            <div className="pt-4">
                <Row className="gx-0">
                    <NavbarWrapper userData={userData?.user} />
                    <Layout />
                    {!!hasJobs && (
                        <div className={"position-fixed bottom-0 end-0 loading-wrapper"}>
                            <LoadingStatus
                                jobList={jobList}
                                isOpen={isLoadingStatusOpen}
                                isVisible={isVisible}
                                onClose={onCloseLoadingImportHandler}
                            />
                        </div>
                    )}
                </Row>
            </div>
        </AccessContext.Provider>
    );
};
